(() => {
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
  const mqMobile = "(max-width: 768.98px)";

  function cssNum(root, varName, fallback) {
    const v = getComputedStyle(root).getPropertyValue(varName).trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function computeTopOffset(fallbackPx = 120) {
    const header =
      document.querySelector("#shopify-section-header") ||
      document.querySelector("header.site-header") ||
      document.querySelector("header");
    const extra = 100;

    if (header) {
      const rect = header.getBoundingClientRect();
      const h = Math.max(rect.height, 40);
      return Math.round(h + extra);
    }

    const w = window.innerWidth;
    if (w < 769) return 68;
    if (w < 1024) return 104;
    return fallbackPx;
  }

  function measureHeights(card, isMobile, root) {
    const wrap = card.querySelector(".media-with-text");
    const mediaBox = card.querySelector(".deck-media__box");
    const content = card.querySelector(".deck-content");

    const wrapCS = wrap ? getComputedStyle(wrap) : null;
    const padY =
      (wrapCS ? parseFloat(wrapCS.paddingTop) : 0) +
      (wrapCS ? parseFloat(wrapCS.paddingBottom) : 0);

    const contentH = content ? content.scrollHeight : 0;

    const IMG_MAX_H_DESKTOP = cssNum(root, "--img-max-h-desktop", 435);
    const IMG_SIZE_MOBILE = cssNum(root, "--img-size-mobile", 270);
    const imgMaxH = isMobile ? IMG_SIZE_MOBILE : IMG_MAX_H_DESKTOP;

    if (mediaBox) mediaBox.style.setProperty("--img-max-h", `${imgMaxH}px`);

    const breathing = isMobile ? 16 : 24;

    const estimated = isMobile
      ? imgMaxH + contentH + padY + breathing
      : Math.max(imgMaxH, contentH) + padY + breathing;

    return { estimated, imgMaxH };
  }

  function sizeStageFromContent(root) {
    const stage = root.querySelector(".deck-cards__stage");
    const cards = stage ? Array.from(stage.querySelectorAll(".deck-card")) : [];
    if (cards.length !== 2) return;

    const isMobile = window.matchMedia(mqMobile).matches;
    const mA = measureHeights(cards[0], isMobile, root);
    const mB = measureHeights(cards[1], isMobile, root);

    const MIN_STAGE = isMobile ? 380 : 340;
    const MAX_VH = Math.round(
      (window.innerHeight || document.documentElement.clientHeight) *
        (isMobile ? 0.9 : 0.78)
    );

    let required = Math.max(mA.estimated, mB.estimated);
    required = clamp(required, MIN_STAGE, Math.max(MIN_STAGE, MAX_VH));

    root.style.setProperty("--deck-height", `${required}px`);

    const vh = window.innerHeight || document.documentElement.clientHeight;
    const scrollExtra = getComputedStyle(root)
      .getPropertyValue("--deck-scroll")
      .trim();
    const extraPx = scrollExtra.endsWith("vh")
      ? (parseFloat(scrollExtra) / 100) * vh
      : parseFloat(scrollExtra || "120");

    root.style.height = `${required + (isNaN(extraPx) ? 0 : extraPx)}px`;

    const autoTop = computeTopOffset(120);
    root.style.setProperty("--deck-top", `${autoTop}px`);
    root.style.setProperty("--deck-gap", `${Math.max(72, autoTop + 8)}px`);
  }

  function enableStickyIfNeeded(root) {
    const mobileSticky = root.getAttribute("data-mobile-sticky") === "true";
    const isMobile = window.matchMedia(mqMobile).matches;
    return isMobile ? mobileSticky : true;
  }

  function initDeck(root) {
    const autoTop = computeTopOffset(120);
    root.style.setProperty("--deck-top", `${autoTop}px`);
    root.style.setProperty("--deck-gap", `${Math.max(72, autoTop + 8)}px`);

    if (!enableStickyIfNeeded(root)) return;

    const stage = root.querySelector(".deck-cards__stage");
    const cards = Array.from(stage.querySelectorAll(".deck-card"));
    if (cards.length !== 2) return;

    const [A, B] = cards;
    A.classList.add("is-front");
    B.classList.add("is-back");

    const rerun = () => {
      sizeStageFromContent(root);
      onScroll();
    };

    sizeStageFromContent(root);

    const imgs = root.querySelectorAll(".deck-img");
    imgs.forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", rerun, { once: true });
    });

    const computeProgress = () => {
      const rect = root.getBoundingClientRect();
      const containerTopInDoc = window.scrollY + rect.top;
      const containerHeight = root.offsetHeight;
      const stageHeight = stage.offsetHeight;
      const travel = Math.max(1, containerHeight - stageHeight);

      const y = Math.min(
        Math.max(window.scrollY - containerTopInDoc, 0),
        travel
      );

      const raw = y / travel;

      const ACTIVE_START = 0.1;
      const ACTIVE_END = 0.9;

      let p = (raw - ACTIVE_START) / (ACTIVE_END - ACTIVE_START);
      p = clamp(p, 0, 1);

      return p;
    };

    let lastSide = "A";
    const onScroll = () => {
      const p = computeProgress();

      const SWITCH = 0.1;
      const SWAP_PAD = 0.18; 

      const swapping = p > SWITCH - SWAP_PAD && p < SWITCH + SWAP_PAD;
      root.classList.toggle("is-swapping", swapping);

      if (p < SWITCH && lastSide !== "A") {
        lastSide = "A";
        A.classList.add("is-front");
        A.classList.remove("is-back");
        B.classList.add("is-back");
        B.classList.remove("is-front");
      } else if (p >= SWITCH && lastSide !== "B") {
        lastSide = "B";
        B.classList.add("is-front");
        B.classList.remove("is-back");
        A.classList.add("is-back");
        A.classList.remove("is-front");
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", rerun);

    document.addEventListener("shopify:section:load", (e) => {
      if (e.target && e.target.contains(root)) rerun();
    });
    document.addEventListener("shopify:block:select", rerun);
    document.addEventListener("shopify:block:deselect", rerun);
    document.addEventListener("shopify:section:unload", (e) => {
      if (e.target && e.target.contains(root)) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", rerun);
      }
    });

    onScroll();
  }

  function boot() {
    document.querySelectorAll(".deck-cards").forEach(initDeck);
  }

  document.addEventListener("DOMContentLoaded", boot);
})();
